import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import type { SelectionListState } from '../../../../shared/selection-lists/types';
import { RevelioPageId } from '../../components/PageConfigProvider/types';
import {
  createDefaultFilterMap,
  type DefaultSelectionOverride,
} from '../contexts/defaultFilters';
import { migrateLegacyPayDefaultMax } from '../definitions/pay';
import type { CustomSkillTaxonomySelection } from '../definitions/skillTaxonomy';
import { getRoleTaxonomyValue } from '../hooks/useAdaptiveRoleTaxonomy';
import { getSkillTaxonomyValue } from '../hooks/useAdaptiveSkillTaxonomy';
import {
  type Filter,
  FilterId,
  type FilterSelectionsMap,
  FilterType,
  type PrimaryFilterId,
  type PrimarySelection,
  type SingleValueFilter,
  type TreeFilter,
} from '../types/filters';
import type { TaxonomyConfig } from '../types/taxonomy';
import { getActiveSelections } from '../utils/primaryFilterUtils';
import { reconcilePageSelections } from './filterReconciliation';

export const NO_PRIMARY_FILTER_KEY = 'NO_PRIMARY_FILTER' as const;
export type ConsistencyKey = string;

const isPrimaryFilterKey = (
  key: ConsistencyKey | undefined
): key is PrimaryFilterId => {
  return (
    key === FilterId.COMPANY ||
    key === FilterId.GEOGRAPHY ||
    key === FilterId.ROLE
  );
};

export interface FilterConsistencyState {
  // Group-level (primary filter)
  groupMap: Map<ConsistencyKey, FilterSelectionsMap>;
  activeKey?: ConsistencyKey;
  // Page-level (template id)
  pageMap: Map<string, Map<FilterId, PrimarySelection[]>>;
}

/**
 * Converts a ConsistencyKey to a PrimaryFilterId for use with createDefaultFilterMap.
 * Returns undefined for keys that don't represent primary filters (NO_PRIMARY_FILTER_KEY).
 * Note: Screener v2 pages now use their corresponding primary filter ID (COMPANY, ROLE, GEOGRAPHY)
 * as the consistency key, so they will be returned directly here.
 */
const getPrimaryFilterIdFromConsistencyKey = (
  key: ConsistencyKey
): PrimaryFilterId | undefined => {
  if (key === NO_PRIMARY_FILTER_KEY) return undefined;
  if (
    key === RevelioPageId.COMPANY_SCREENER_V2 ||
    key === RevelioPageId.GEOGRAPHY_SCREENER_V2 ||
    key === RevelioPageId.ROLE_SCREENER_V2
  ) {
    return undefined;
  }
  if (!isPrimaryFilterKey(key)) return undefined;
  return key;
};

const migrateFilterSelectionsMap = (
  map: FilterSelectionsMap
): FilterSelectionsMap =>
  new Map(
    Array.from(map.entries()).map(([id, filter]) => [
      id,
      migrateLegacyPayDefaultMax(filter),
    ])
  );

export const createInitialFilterConsistencyState = (
  defaultKey: ConsistencyKey,
  taxonomyConfig?: TaxonomyConfig,
  defaultSelections?: DefaultSelectionOverride
): FilterConsistencyState => {
  const initialMap = new Map<ConsistencyKey, FilterSelectionsMap>();

  initialMap.set(
    defaultKey,
    createDefaultFilterMap(
      getPrimaryFilterIdFromConsistencyKey(defaultKey),
      taxonomyConfig,
      defaultSelections
    )
  );

  return {
    groupMap: initialMap,
    activeKey: defaultKey,
    pageMap: new Map(),
  };
};

export const applyFilterAction = (
  state: FilterConsistencyState,
  filterIdToUpdate: FilterId,
  updatePayload: Partial<Filter> | Filter,
  isSetOperation: boolean,
  groupKeyOverride?: ConsistencyKey
): { nextState: FilterConsistencyState; updatedFilter: Filter | null } => {
  const { activeKey } = state;
  const groupKey = groupKeyOverride ?? activeKey;
  if (!groupKey) return { nextState: state, updatedFilter: null };

  const currentActiveMap = state.groupMap.get(groupKey);
  if (!currentActiveMap) return { nextState: state, updatedFilter: null };

  const newActiveMap = new Map(currentActiveMap);
  let updatedFilter: Filter | null = null;

  // SET_FILTER operation
  if (isSetOperation) {
    const filterToSet = updatePayload as Filter;
    newActiveMap.set(filterIdToUpdate, filterToSet);
    updatedFilter = filterToSet;
  } else {
    // UPDATE_FILTER operation
    const existingFilter = newActiveMap.get(filterIdToUpdate);
    if (existingFilter) {
      updatedFilter = { ...existingFilter, ...updatePayload } as Filter;
      newActiveMap.set(filterIdToUpdate, updatedFilter);
    }
  }

  if (updatedFilter) {
    const migratedFilter = migrateLegacyPayDefaultMax(updatedFilter);
    newActiveMap.set(filterIdToUpdate, migratedFilter);
    const newConsistencyMap = new Map(state.groupMap);
    newConsistencyMap.set(groupKey, newActiveMap);
    return {
      nextState: { ...state, groupMap: newConsistencyMap },
      updatedFilter: migratedFilter,
    };
  }

  return { nextState: state, updatedFilter: null };
};

const extractKeyFromFilters = (
  filters: FilterSelectionsMap | undefined
): PrimaryFilterId | undefined => {
  if (!filters) return undefined;

  return Array.from(filters.entries()).find(
    ([, f]) => 'isPrimary' in f && f.isPrimary === true
  )?.[0] as PrimaryFilterId | undefined;
};

export const getActiveFilterMap = (
  state: FilterConsistencyState,
  taxonomyConfig?: TaxonomyConfig
): FilterSelectionsMap => {
  if (!state.activeKey) {
    return createDefaultFilterMap(undefined, taxonomyConfig);
  }

  const activeMap = state.groupMap.get(state.activeKey);
  if (activeMap) {
    return activeMap;
  }

  if (!isPrimaryFilterKey(state.activeKey)) {
    return createDefaultFilterMap(undefined, taxonomyConfig);
  }

  return createDefaultFilterMap(
    getPrimaryFilterIdFromConsistencyKey(state.activeKey),
    taxonomyConfig
  );
};

export const getOrCreateFilters = (
  filterState: FilterConsistencyState,
  key: ConsistencyKey,
  taxonomyConfig?: TaxonomyConfig
): FilterSelectionsMap => {
  const existing = filterState.groupMap.get(key);
  if (existing) {
    return existing;
  }

  const filterMap = createDefaultFilterMap(
    getPrimaryFilterIdFromConsistencyKey(key),
    taxonomyConfig
  );

  return filterMap;
};

/**
 * Generates a page key by combining the consistency key (group key) with the page ID.
 * Format: `${groupKey}:${pageId}`
 * This allows filter state to be isolated per page while sharing the same consistency group.
 */
export const generatePageKey = (
  groupKey: ConsistencyKey,
  pageId: string | undefined = 'UNKNOWN_PAGE'
): string => {
  return `${groupKey}:${pageId}`;
};

/**
 * Type guard to check if a string is a valid PrimaryFilterId.
 */
const isPrimaryFilterId = (key: string): key is PrimaryFilterId => {
  return (
    key === FilterId.COMPANY ||
    key === FilterId.GEOGRAPHY ||
    key === FilterId.ROLE
  );
};

/**
 * Extracts the consistency key from a pageKey string.
 * Page keys are formatted as `${groupKey}:${pageId}`, so we extract the groupKey part.
 * This is necessary because screener v2 pages use their page ID as the consistency key,
 * but the filter map doesn't contain a primary filter to derive it from.
 */
const extractConsistencyKeyFromPageKey = (
  pageKey: string
): ConsistencyKey | undefined => {
  const [groupKey] = pageKey.split(':');
  if (!groupKey) return undefined;

  if (groupKey === NO_PRIMARY_FILTER_KEY) {
    return NO_PRIMARY_FILTER_KEY;
  }

  // Check if it's a screener v2 page ID (these pages use page ID as consistency key)
  if (
    groupKey === RevelioPageId.COMPANY_SCREENER_V2 ||
    groupKey === RevelioPageId.GEOGRAPHY_SCREENER_V2 ||
    groupKey === RevelioPageId.ROLE_SCREENER_V2
  ) {
    return groupKey;
  }

  // Check if it's a primary filter ID
  if (isPrimaryFilterId(groupKey)) {
    return groupKey;
  }

  return undefined;
};

/**
 * Handles replacing the entire set of active filters.
 * Initializes filter group and page maps for filter consistency.
 */
export const handleReplaceFilters = (
  state: FilterConsistencyState,
  payload: {
    filterMap: FilterSelectionsMap;
    pageKey: string;
    defaultActiveCount?: number;
    skipReconciliation?: boolean;
  }
): FilterConsistencyState => {
  const {
    filterMap: rawFilterMap,
    pageKey,
    defaultActiveCount = -1,
    skipReconciliation,
  } = payload;

  const filterMap = migrateFilterSelectionsMap(rawFilterMap);

  const primaryFilterId = extractKeyFromFilters(filterMap);
  // Extract consistency key from pageKey first (needed for screener v2 pages which don't have primary filters)
  // Fall back to primaryFilterId from filter map, then NO_PRIMARY_FILTER_KEY
  const activeKeyFromPageKey = extractConsistencyKeyFromPageKey(pageKey);
  const activeKey: ConsistencyKey =
    activeKeyFromPageKey ?? primaryFilterId ?? NO_PRIMARY_FILTER_KEY;

  const primaryFilter = primaryFilterId
    ? (filterMap.get(primaryFilterId) as TreeFilter | undefined)
    : undefined;
  const incomingSelections = primaryFilter?.primarySelections || [];

  const existingPageSelections = primaryFilterId
    ? state.pageMap.get(pageKey)?.get(primaryFilterId) || []
    : [];

  const finalizedPageSelections = skipReconciliation
    ? incomingSelections
    : reconcilePageSelections(
        existingPageSelections,
        incomingSelections,
        defaultActiveCount
      );

  // Create the group map for the new active group
  const finalGroupFilterMap = new Map(filterMap);
  if (primaryFilter && primaryFilterId) {
    finalGroupFilterMap.set(primaryFilterId, {
      ...primaryFilter,
      primarySelections: finalizedPageSelections,
      selections: getActiveSelections(finalizedPageSelections),
    });
  }

  // Construct the new state
  const newGroupMap = new Map(state.groupMap);
  newGroupMap.set(activeKey, finalGroupFilterMap);

  const newPageFilterMap = new Map(state.pageMap.get(pageKey) || []);
  if (primaryFilter && primaryFilterId) {
    // If primarySelections is empty, delete the entry to trigger PrimaryFilter auto-reset
    // Otherwise, set it to the finalized selections
    if (finalizedPageSelections.length === 0) {
      newPageFilterMap.delete(primaryFilterId);
    } else {
      newPageFilterMap.set(primaryFilterId, finalizedPageSelections);
    }
  }
  const newPageMap = new Map(state.pageMap);
  newPageMap.set(pageKey, newPageFilterMap);

  return {
    groupMap: newGroupMap,
    activeKey,
    pageMap: newPageMap,
  };
};

export const addNewFilterGroup = ({
  filterState,
  newFilterKey,
  newFilterMap,
}: {
  filterState: FilterConsistencyState;
  newFilterKey: ConsistencyKey;
  newFilterMap: FilterSelectionsMap;
}): FilterConsistencyState => {
  const newGroupMap = new Map(filterState.groupMap);
  newGroupMap.set(newFilterKey, migrateFilterSelectionsMap(newFilterMap));

  return { ...filterState, groupMap: newGroupMap };
};

export const createNewFilterState = ({
  primaryFilterId,
  pageId,
  selectionDefault,
  taxonomyConfig,
  ricsK400List,
  defaultSelections,
}: {
  primaryFilterId: PrimaryFilterId;
  pageId: string;
  selectionDefault: number;
  taxonomyConfig?: TaxonomyConfig;
  ricsK400List?: SelectionListState<'rics_k400'>['rics_k400'];
  defaultSelections?: DefaultSelectionOverride;
}): FilterConsistencyState => {
  const defaultFilterState = createInitialFilterConsistencyState(
    primaryFilterId,
    taxonomyConfig,
    defaultSelections
  );

  const filterMap = defaultFilterState.groupMap.get(primaryFilterId);

  const filterState = filterMap
    ? handleReplaceFilters(defaultFilterState, {
        filterMap: filterMap,
        pageKey: generatePageKey(primaryFilterId, pageId),
        defaultActiveCount: selectionDefault,
      })
    : defaultFilterState;

  const adaptiveTaxonomyFilterState = (() => {
    if (primaryFilterId !== FilterId.COMPANY) {
      return null;
    }

    const filterSelectionsMap = filterState.groupMap?.get(FilterId.COMPANY);
    if (!filterSelectionsMap) return null;

    const companyFilterState = getTreeFilter(
      filterSelectionsMap,
      FilterId.COMPANY
    );

    if (!companyFilterState) return null;

    const roleTaxonomyValue = {
      ...getRoleTaxonomyValue(companyFilterState, ricsK400List ?? []),
      version: taxonomyConfig?.roleTaxonomyVersion,
    };

    const newFilterMap = new Map(filterState.groupMap.get(FilterId.COMPANY));
    const defaultRoleTaxonomyFilter = newFilterMap.get(
      FilterId.ROLE_TAXONOMY
    ) as SingleValueFilter<CustomRoleTaxonomySelection> | undefined;
    if (!defaultRoleTaxonomyFilter) return null;

    newFilterMap.set(FilterId.ROLE_TAXONOMY, {
      ...defaultRoleTaxonomyFilter,
      value: roleTaxonomyValue,
    });

    const defaultSkillTaxonomyFilter = newFilterMap.get(
      FilterId.SKILL_TAXONOMY
    ) as SingleValueFilter<CustomSkillTaxonomySelection> | undefined;

    if (!defaultSkillTaxonomyFilter) return null;

    const skillTaxonomyValue = {
      ...getSkillTaxonomyValue(companyFilterState, ricsK400List ?? []),
      version: taxonomyConfig?.skillTaxonomyVersion,
    };

    newFilterMap.set(FilterId.SKILL_TAXONOMY, {
      ...defaultSkillTaxonomyFilter,
      value: skillTaxonomyValue,
      isUserSubmitted: false,
    });

    return newFilterMap;
  })();

  return adaptiveTaxonomyFilterState
    ? handleReplaceFilters(filterState, {
        filterMap: adaptiveTaxonomyFilterState,
        pageKey: generatePageKey(primaryFilterId, pageId),
        defaultActiveCount: selectionDefault,
      })
    : filterState;
};

const getTreeFilter = (
  filterState: FilterSelectionsMap,
  filterId: FilterId
) => {
  const filter = filterState.get(filterId);
  if (!filter || filter.type !== FilterType.TREE) return null;
  return filter;
};
